<template>
  <div class="form-container-input">
    <label v-if="label && !iconLabel" :class="`label-size-${labelSize}`">
      {{ label }}
    </label>
    <div v-if="label && iconLabel" class="label-icon">
      <i :class="iconLabel"></i>
      <label>
        {{ label }}
      </label>
    </div>
    <input
      v-if="inputType === 'input'"
      :value="value"
      class="form-control"
      :class="{ 'is-invalid': submitted && invalid }"
      :readonly="readOnly"
      :placeholder="placeholder"
      :type="passwordType"
      @input="$emit('input', $event.target.value)"
    />
    <input
      v-if="inputType === 'time'"
      :value="value"
      class="form-control"
      :class="{ 'is-invalid': submitted && invalid }"
      :readonly="readOnly"
      :placeholder="placeholder"
      type="time"
      @input="$emit('input', $event.target.value)"
    />
    <input
      v-if="inputType === 'number'"
      :value="value"
      class="form-control"
      :class="{ 'is-invalid': submitted && invalid }"
      :readonly="readOnly"
      :placeholder="placeholder"
      type="number"
      :min="min"
      :max="max"
      @input="$emit('input', $event.target.value)"
    />
    <textarea
      v-if="inputType === 'textarea'"
      ref="textarea"
      :value="value"
      :disabled="readOnly"
      class="form-control"
      :class="{ 'is-invalid': submitted && invalid }"
      :style="`height: ${textAreaHeight}px`"
      @input="$emit('input', $event.target.value)"
    >
    </textarea>
    <h-f-tiptap v-if="inputType === 'tip-tap'" v-model="ckeditorValue" />
    <ckeditor
      v-if="inputType === 'ckeditor'"
      v-model="ckeditorValue"
      class="ck-editor form-control"
      :class="{ 'is-invalid': submitted && invalid }"
      :editor="editor"
      :readonly="readOnly"
    >
    </ckeditor>
    <v-select
      v-if="inputType === 'model-select'"
      v-model="modelSelectValue"
      label="text"
      :options="modelSelect"
      :reduce="reduce ? (option) => option.value : (option) => option"
      :create-option="(option) => ({ value: option, text: option })"
      :taggable="taggable"
      class="model-select"
      :disabled="readOnly"
      :selectable="() => !readOnly"
      @search="$emit('searchchange', $event)"
    >
      <template #no-options="{ searching }">
        <template v-if="searching"> Aucune donnée trouvée.</template>
      </template>
    </v-select>
    <v-select
      v-if="inputType === 'multi-select'"
      v-model="modelSelectValue"
      label="text"
      :options="multiSelect"
      :reduce="reduce ? (option) => option.value : (option) => option"
      multiple
      :taggable="taggable"
      class="model-select"
      :disabled="readOnly"
      :selectable="() => !readOnly"
      @select="handleSelect"
      @search="$emit('searchchange', $event)"
    >
      <template #no-options="{ searching }">
        <template v-if="searching"> Aucune donnée trouvée.</template>
      </template>
    </v-select>
    <h-f-search-field
      v-if="inputType === 'search-field'"
      ref="searchField"
      v-model="searchFieldValue"
      :class="{ 'is-invalid': submitted && invalid }"
      :base-url="baseUrl"
      :ressource="ressource"
      :fields="fields"
      :text-values="textValues"
      :placeholder="placeholder"
    >
    </h-f-search-field>
    <!-- WARNING: Date picker always return datetime type -->
    <date-picker
      v-if="inputType === 'date-time'"
      v-model="dateValue"
      :min-date="minDate"
      :max-date="maxDate"
      mode="dateTime"
      is24hr
      class="w-100"
    >
      <template #default="{ inputValue, inputEvents }">
        <input
          class="px-2 py-1 rounded w-100"
          :value="inputValue"
          :readonly="readOnly"
          :placeholder="placeholder"
          style="border: 1px solid #eeeffb !important"
          v-on="inputEvents"
        />
      </template>
    </date-picker>
    <date-picker
      v-if="inputType === 'date'"
      v-model="dateValue"
      :min-date="minDate"
      :max-date="maxDate"
      mode="date"
      is24hr
      class="w-100"
    >
      <template #default="{ inputValue, inputEvents }">
        <input
          class="px-2 py-1 rounded w-100"
          :value="inputValue"
          :placeholder="placeholder"
          :readonly="readOnly"
          style="border: 1px solid #eeeffb !important"
          v-on="inputEvents"
        />
      </template>
    </date-picker>
    <date-picker v-if="inputType === 'date-no-pop'" v-model="dateValue" :min-date="minDate" :max-date="maxDate" />
    <date-picker
      v-if="inputType === 'date-range'"
      v-model="dateValue"
      :min-date="minDate"
      :max-date="maxDate"
      is-range
      :model-config="modelConfig"
    />
    <div v-if="inputType === 'social-media'" class="container-icone-input">
      <i :class="'mdi mdi-' + socialMedia + ' text-primary bx-sm'"></i>
      <input
        :value="value"
        class="form-control"
        :readonly="readOnly"
        :class="{ 'is-invalid': submitted && invalid }"
        @input="$emit('input', $event.target.value)"
      />
    </div>
    <div v-if="inputType === 'unit'" class="unit-input">
      <input
        :value="value"
        class="form-control"
        :class="{ 'is-invalid': submitted && invalid }"
        :readonly="readOnly"
        :placeholder="placeholder"
        @input="$emit('input', $event.target.value)"
      />
      <div class="unit-container">
        {{ unit }}
      </div>
    </div>
    <vue-dropzone
      v-if="inputType === 'dropzone'"
      id="dropzone"
      ref="myVueDropzone"
      :use-custom-slot="true"
      :options="dropzoneOptions"
      @vdropzone-file-added="vfileAdded"
    >
      <div class="dropzone-custom-content">
        <i class="display-4 text-muted bx bx-cloud-download"></i>
        <h5>Choisir ou déposer un fichier</h5>
      </div>
    </vue-dropzone>
    <div v-if="inputType === 'multiple-input'" class="w-100">
      <div v-for="index in numberInputValues" :key="index" class="form-add">
        <input v-if="inputTypeMultipleFiled === 'input'" v-model="multipleInputValues[index - 1]" />
        <model-select
          v-if="inputTypeMultipleFiled === 'model-select'"
          v-model="multipleInputValues[index - 1]"
          :options="modelSelect"
          placeholder="Choisir ..."
          @searchchange="$emit('searchchange', $event)"
        ></model-select>
        <h-f-model-multi-taggable
          v-if="inputTypeMultipleFiled === 'search-field'"
          ref="searchField"
          v-model="multipleInputValues[index - 1]"
          :class="{ 'is-invalid': submitted && invalid }"
          :base-url="baseUrl"
          :ressource="ressource"
          :text-choices="textValues"
          :placeholder="placeholder"
          :additional-request="additionalRequest"
          class="w-100"
        >
        </h-f-model-multi-taggable>
        <div class="buttons">
          <i
            v-if="index === multipleInputValues.length || multipleInputValues.length === 0"
            class="mdi mdi-plus-circle-outline bx-sm"
            @click="addInputValue(index)"
          >
          </i>
          <i v-if="numberInputValues !== 1" class="mdi mdi-minus-circle-outline bx-sm" @click="removeInputValue(index)">
          </i>
        </div>
      </div>
    </div>
    <vue-google-autocomplete
      v-if="inputType === 'address'"
      id="map"
      ref="toAddress"
      classname="form-control"
      placeholder="Rentrez une adresse"
      country="fr"
      @placechanged="handleAddress"
    >
    </vue-google-autocomplete>

    <vue-tel-input
      v-if="inputType === 'phone'"
      v-model="phoneValue"
      default-country="fr"
      placeholder="Rentrez un numéro de téléphone"
    ></vue-tel-input>

    <div v-if="invalid && submitted" class="invalid-feedback many m-0">
      <span v-if="invalid && required"> Ce champ est requis </span>
      <span v-if="invalid && email"> Ce champ doit être un email </span>
      <span v-if="invalid && numeric"> Ce champ doit contenir des chiffres uniquement </span>
      <span v-if="invalid && url"> Ce champ doit être un URL </span>
      <span v-if="invalid && maxLength"> La saisie contient actuellement trop de caractères </span>
      <span v-if="invalid && minLength"> La saisie ne continent actuellement pas assez de caractères </span>
      <span v-if="invalid && minValue"> Ce champ doit être supérieur ou égale à {{ minValueRequired }} </span>
      <span v-if="invalid && maxValue"> Ce champ doit être inférieur ou égale à {{ maxValueRequired }} </span>
      <span v-if="invalid && decimal"> Ce champ doit contenir un nombre décimal </span>
    </div>
    <p v-if="description">
      {{ description }}
    </p>
  </div>
</template>

<script>
import VueGoogleAutocomplete from 'vue-google-autocomplete'
import CKEditor from '@ckeditor/ckeditor5-vue2'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import 'vue-search-select/dist/VueSearchSelect.css'
import moment from 'moment'
import 'moment/locale/fr'
import vue2Dropzone from 'vue2-dropzone'
import vSelect from 'vue-select'
import { APIRequest } from '@/node-sdk'

import DatePicker from 'v-calendar/lib/components/date-picker.umd' // without this line it didn't work
import HFSearchField from './search-field'

import 'vue-select/dist/vue-select.css'

moment.locale('fr')

export default {
  name: 'input-c',

  components: {
    VueGoogleAutocomplete,
    ckeditor: CKEditor.component,
    HFSearchField,
    vueDropzone: vue2Dropzone,
    DatePicker,
    vSelect,
  },

  props: {
    // eslint-disable-next-line vue/require-default-prop
    value: [Date, String, Number, Array, Object, Boolean],
    label: {
      type: String,
      default: '',
    },
    labelSize: {
      type: String,
      default: 'lg',
    },
    iconLabel: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    additionalRequest: {
      type: String,
      default: '',
    },
    inputType: {
      type: String,
      default: 'input',
    },
    textAreaHeight: {
      type: Number,
      default: 60,
    },
    placeholder: {
      type: String,
      default: '',
    },
    inputTypeMultipleFiled: {
      type: String,
      default: 'input',
    },
    modelConfig: {
      type: Object,
      default: null,
    },
    defaultValue: {
      type: String,
      default: '',
    },
    socialMedia: {
      type: String,
      default: '',
    },
    unit: {
      type: String,
      default: '',
    },
    modelSelect: {
      type: Array,
      default: () => [],
    },
    multiSelect: {
      type: Array,
      default: () => [],
    },
    baseUrl: {
      type: String,
      default: '',
    },
    ressource: {
      type: String,
      default: '',
    },
    fields: {
      type: String,
      default: '',
    },
    textValues: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: false,
    },

    invalid: {
      type: Boolean,
      default: false,
    },
    submitted: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    email: {
      type: Boolean,
      default: false,
    },
    numeric: {
      type: Boolean,
      default: false,
    },
    url: {
      type: Boolean,
      default: false,
    },
    maxLength: {
      type: Boolean,
      default: false,
    },
    minLength: {
      type: Boolean,
      default: false,
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    // eslint-disable-next-line vue/require-default-prop
    minDate: [Date, String],
    // eslint-disable-next-line vue/require-default-prop
    maxDate: [Date, String],
    // eslint-disable-next-line vue/require-default-prop
    min: String,
    // eslint-disable-next-line vue/require-default-prop
    max: String,
    reduce: {
      type: Boolean,
      default: true,
    },
    minValueRequired: {
      type: [Number, Date],
      default: 0,
    },
    maxValueRequired: {
      type: [Number, Date],
      default: 0,
    },
    decimal: {
      type: Boolean,
      default: false,
    },
    maxValue: {
      type: Boolean,
      default: false,
    },
    minValue: {
      type: Boolean,
      default: false,
    },
    passwordType: {
      type: String,
      default: 'text',
    },
  },

  data() {
    return {
      editor: ClassicEditor,
      ckeditorValue: this.value,
      modelSelectValue: this.value,
      multipleInputValues: this.value,
      searchFieldValue: this.value,
      dateValue: this.value,
      phoneValue: this.value,
      dropzoneValues: this.value,
      multiSelectValues: this.value,
      timeValue: this.value,
      numberInputValues: 1,
      formDocument: {},
      dropzoneOptions: {
        url: 'https://httpbin.org/post',
        thumbnailWidth: 100,
        maxFilesize: 5.0,
        headers: { 'My-Awesome-Header': 'header value' },
        acceptedFiles: 'image/*, .psd, .pdf',
      },
      editorConfig: {
        toolbar: false,
      },
      phoneConfig: {
        placeholder: 'Rentre',
      },
    }
  },

  watch: {
    ckeditorValue() {
      this.handleCkeditor()
    },

    modelSelectValue() {
      this.handleModelSelectValue()
    },

    multiSelectValues() {
      this.handleMultiSelectValues()
    },

    searchFieldValue() {
      this.handleSearchFieldValue()
    },

    dateValue() {
      this.handleDateValue()
    },

    phoneValue() {
      this.handlePhoneValue()
    },

    value() {
      if (this.inputType === 'ckeditor' || this.inputType === 'ckeditor-no-toolbar' || this.inputType === 'tip-tap') {
        this.ckeditorValue = this.value
      } else if (this.inputType === 'model-select') {
        this.modelSelectValue = this.value
      } else if (this.inputType === 'multiple-input') {
        this.multipleInputValues = this.value
        if (this.multipleInputValues.length !== 0) {
          this.numberInputValues = this.multipleInputValues.length
        } else {
          this.numberInputValues = 1
        }
      } else if (this.inputType === 'search-field') {
        this.searchFieldValue = this.value
      } else if (this.inputType === 'date-time' || this.inputType === 'date' || this.inputType === 'date-range') {
        this.dateValue = this.value
      } else if (this.inputType === 'multi-select') {
        this.multiSelectValues = this.value
      } else if (this.inputType === 'phone') {
        this.phoneValue = this.value
      } else if (this.inputType === 'address') {
        this.$refs.toAddress.update(this.value)
      }
    },
  },

  mounted() {
    if (this.inputType === 'multiple-input') {
      if (this.multipleInputValues.length !== 0) {
        this.numberInputValues = this.value.length
      } else {
        this.numberInputValues = 1
      }
    }
  },

  methods: {
    handleCkeditor() {
      this.$emit('input', this.ckeditorValue)
    },

    handleModelSelectValue() {
      this.$emit('input', this.modelSelectValue)
    },

    handleMultiSelectValues() {
      this.$emit('input', this.multiSelectValues)
    },

    handleSearchFieldValue() {
      this.$emit('input', this.searchFieldValue)
    },

    handlePhoneValue() {
      this.$emit('input', this.phoneValue)
    },

    handleDateValue() {
      this.$emit('input', this.dateValue)
      this.$emit('change')
    },

    addInputValue(index) {
      if (this.multipleInputValues[index - 1] !== '' && this.multipleInputValues.length === index) {
        this.numberInputValues += 1
        this.multipleInputValues.push('')
      }
    },

    removeInputValue(index) {
      this.multipleInputValues.splice(index - 1, 1)
      this.numberInputValues -= 1
    },

    async vfileAddedPromise(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = (event) => {
          this.formDocument.document = event.target.result
          resolve(reader.result)
        }

        reader.onerror = reject

        reader.readAsDataURL(file)
      })
    },

    async vfileAdded(file) {
      this.formDocument.title = file.name
      this.formDocument.name = file.name
      const size = file.size / 10 ** 6
      this.formDocument.size = size.toFixed(1)
      await this.vfileAddedPromise(file)
      this.dropzoneValues.push(this.formDocument)
      this.formDocument = {}
      this.$emit('input', this.dropzoneValues)
    },

    async postDocuments(baseUrl, ressource, entity, entityId) {
      const documents = []
      for (let i = 0; i < this.dropzoneValues.length; i++) {
        this.dropzoneValues[i][entity] = entityId
        // eslint-disable-next-line no-await-in-loop
        const response = await APIRequest.create(baseUrl, ressource, this.dropzoneValues[i])
        documents.push(response)
      }
      this.$refs.myVueDropzone.removeAllFiles()
      return documents
    },

    async postDocumentsV2(service, ressource, entity, entitiId) {
      const { Hk } = await import('@/node-sdk')
      const resourceGetter = Hk[service][ressource]
      const documents = this.dropzoneValues.map(async (dropzonevalue) => {
        return resourceGetter.create({ ...dropzonevalue, [entity]: entitiId })
      })
      await Promise.all(documents)
      this.$refs.myVueDropzone.removeAllFiles()
      return documents
    },

    handleSelect(items) {
      this.$emit('select', items, this.multiSelectValues)
      this.multiSelectValues = items
    },

    handleClickOutside() {
      this.$emit('clickOutside')
    },
    handleAddress(addressData) {
      this.$emit('input', addressData)
      this.$emit(
        'address',
        `${addressData.street_number} ${addressData.route} ${addressData.locality} ${addressData.postal_code} ${addressData.country}`,
      )
    },
  },
}
</script>

<style lang="scss">
.model-select {
  background-color: #f7f7fd;
  --vs-border-color: #eeeffb;
  --vs-controls-size: 0.7;

  input {
    height: 20px !important;
  }

  .vs__search {
    background-color: #f7f7fd;
    border: none;
  }
}
</style>
